import * as Yup from 'yup';

import { FormikHelpers, FormikValues } from 'formik';

import { ApiHelper } from '../../helpers';
import { Axios } from 'axios';
import { BuscaPersonalizadaGrupoEleitoresFormType } from '../../types';
import { FunctionComponent } from 'react';
import { StatusCodes } from 'http-status-codes';
import { TextAreaField } from '../../components';
import { TipoGrupoEleitoresEnum } from '../../enums';
import { TipoGrupoEleitoresEnumType } from '../../types/enums/TipoGrupoEleitoresEnumType';
import { useTranslation } from 'react-i18next';

const LIMITE_QTD_MATRICULAS = 1000;

export const initialValuesBuscaPersonalizadaGrupoEleitoresForm = (
  t: Function,
  tipoGrupoEleitores: TipoGrupoEleitoresEnumType,
) => ({
  matriculas: '',
  selectedForm:
    tipoGrupoEleitores === TipoGrupoEleitoresEnum.DISCENTE
      ? t('label.discentes_ativos_por_matricula')
      : tipoGrupoEleitores === TipoGrupoEleitoresEnum.DOCENTE_EXTERNO
        ? t('label.docentes_externos_por_matricula')
        : tipoGrupoEleitores === TipoGrupoEleitoresEnum.DOCENTE
          ? t('label.docentes_por_siape')
          : t('label.tecnicos_administrativos_por_siape'),
});

export const validationSchemaBuscaPersonalizadaGrupoEleitoresForm = (
  t: Function,
  tipoGrupoEleitores: TipoGrupoEleitoresEnumType,
) =>
  Yup.object().shape({
    matriculas: Yup.string()
      .required(t('validacao.campo_obrigatorio'))
      .test(
        'isEveryMatriculaValid',
        t(
          `validacao.${
            tipoGrupoEleitores === TipoGrupoEleitoresEnum.DISCENTE ||
            tipoGrupoEleitores === TipoGrupoEleitoresEnum.DOCENTE_EXTERNO
              ? 'matriculas_invalidas'
              : 'matriculas_siape_invalidas'
          }`,
        ),
        (value: any) => {
          const maxMatriculaDiscente = 99999999999;
          const maxMatriculaSiape = 9999999;
          const matriculas = value && value.split('\n').filter((matricula: string) => !!matricula.trim());
          return (
            !matriculas ||
            matriculas.every(
              (matricula: string) =>
                Number.isInteger(+matricula) &&
                +matricula >= 0 &&
                +matricula <=
                  (tipoGrupoEleitores === TipoGrupoEleitoresEnum.DISCENTE ? maxMatriculaDiscente : maxMatriculaSiape),
            )
          );
        },
      )
      .test(
        'isCountMatriculasLessThanLimit',
        t('validacao.matriculas_limite_excedido', { max_matriculas: LIMITE_QTD_MATRICULAS }),
        (value: any) => {
          const matriculas = value && value.split('\n').filter((matricula: string) => !!matricula.trim());
          return matriculas && matriculas.length <= LIMITE_QTD_MATRICULAS;
        },
      ),
  });

export const onSubmitBuscaPersonalizadaGrupoEleitoresForm =
  (
    t: Function,
    axios: Axios,
    setResultado: Function,
    setLoading: Function,
    setErrorStatus: Function,
    errorHandler: Function,
    tipoGrupoEleitores: TipoGrupoEleitoresEnumType,
  ) =>
  (values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => {
    let resultadoBusca: string[] = [];
    let promises: Promise<number | void>[] = [];
    const partitionSize = 50;
    const matriculas = values.matriculas.split('\n').filter((matricula: string) => !!matricula.trim());
    if (matriculas?.length === 0) {
      formikHelpers.setSubmitting(false);
      return;
    }
    let partitions = matriculas.reduce((result: string[][], item: string, index: number) => {
      const partitionIndex = Math.floor(index / partitionSize);
      if (!result[partitionIndex]) {
        result[partitionIndex] = [];
      }
      result[partitionIndex].push(item);
      return result;
    }, []);
    setLoading(true);
    partitions.forEach((partition: string[]) =>
      promises.push(
        axios
          .get(ApiHelper.getUrl.apiSCE.buscaEleitores(tipoGrupoEleitores), {
            params: { consulta: 'PERSONALIZADA', matriculas: partition.join(',') },
          })
          .then((response) => {
            resultadoBusca.push(...response.data);
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_eleitores'));
            }
          }),
      ),
    );

    Promise.all(promises)
      .then(() => {
        setResultado(resultadoBusca);
      })
      .catch((error) => errorHandler(error, t('erro.recuperar_eleitores')))
      .finally(() => {
        formikHelpers.setSubmitting(false);
        setLoading(false);
      });
  };

const BuscaPersonalizadaGrupoEleitoresForm: FunctionComponent<BuscaPersonalizadaGrupoEleitoresFormType> = ({
  formikProps,
  tipoGrupoEleitores,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <TextAreaField
        name="matriculas"
        label={t(
          `label.${
            tipoGrupoEleitores === TipoGrupoEleitoresEnum.DISCENTE ||
            tipoGrupoEleitores === TipoGrupoEleitoresEnum.DOCENTE_EXTERNO
              ? 'matriculas'
              : 'matriculas_siape'
          }`,
        )}
        formikProps={formikProps}
        required
      />
    </div>
  );
};

export default BuscaPersonalizadaGrupoEleitoresForm;
